import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, TriangleAlert } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities, useMe } from "../../api/queries.ts";
import type { Coupon } from "../coupons.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 优惠券
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **「这张券花了我多少」要单独成一列。**
 *    谁发的券谁掏钱 —— 券让买家少付的部分记成发券方名下的一笔负分账。
 *    手机上这个数混在一段小字里，网页上排成一列**从上往下就能看出
 *    哪张券在烧钱**，而这正是发券方最该盯的一个数。
 *
 * ⚠️ 发券表单在网页上展开在列表上方，列表照旧在下面 ——
 *    发新券时常常要照着已有的券填（券码规则、门槛），把列表藏起来是帮倒忙。
 */
export function DesktopCoupons() {
  const search = useSearch({ from: "/coupons" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const qc = useQueryClient();

  /** 能发券的是链上的转售方：平台、经销商、分销商 —— 跟定价是同一批。 */
  const orgs = (identities?.orgs ?? []).filter(
    (o) =>
      o.status === "active" &&
      (o.type === "platform" || o.type === "dealer" || o.type === "distributor"),
  );
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    kind: "fixed" as "fixed" | "percent",
    value: "",
    threshold: "",
    totalCount: "",
  });

  const list = useQuery({
    queryKey: ["coupons", orgId],
    queryFn: () => orgFetch<Coupon[]>("/coupons", orgId!),
    enabled: Boolean(orgId),
  });

  const create = useMutation({
    mutationFn: () =>
      orgFetch("/coupons", orgId!, {
        method: "POST",
        body: JSON.stringify({
          code: form.code.toUpperCase(),
          name: form.name,
          kind: form.kind,
          /** ⚠️ 满减填的是元、打折填的是折扣数，两条路不能混着换算。 */
          value: form.kind === "fixed" ? Math.round(Number(form.value) * 100) : Number(form.value),
          threshold: form.threshold ? Math.round(Number(form.threshold) * 100) : 0,
          totalCount: form.totalCount ? Number(form.totalCount) : 0,
        }),
      }),
    onSuccess: () => {
      setOpen(false);
      setForm({ code: "", name: "", kind: "fixed", value: "", threshold: "", totalCount: "" });
      void qc.invalidateQueries({ queryKey: ["coupons", orgId] });
    },
  });

  const toggle = useMutation({
    mutationFn: (c: Coupon) =>
      orgFetch(`/coupons/${c.id}/toggle`, orgId!, {
        method: "POST",
        body: JSON.stringify({ active: !c.active }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coupons", orgId] }),
  });

  if (mePending)
    return (
      <AccountShell title="优惠券">
        <Spinner compact />
      </AccountShell>
    );

  if (!me || orgs.length === 0)
    return (
      <AccountShell title="优惠券">
        <Empty title="你名下没有可发券的店" hint="发券是商家功能，联系上级商家或平台开通" compact />
      </AccountShell>
    );

  return (
    <AccountShell title="优惠券">
      {orgs.length > 1 && (
        <div className="mb-4 flex gap-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                o.id === orgId
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {/* 🔴 这条提示不是客套话：券比差价大就是卖一单亏一单。 */}
      <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-[14px] leading-relaxed text-amber-800">
        <p className="flex items-center gap-2 font-medium">
          <TriangleAlert className="size-4" />
          券的钱从<strong>你自己的差价</strong>里出
        </p>
        <p className="mt-1">
          面额比你在那件商品上赚的还多的话，这一单你是净亏的。发之前对一下「我的定价」。
        </p>
      </div>

      {open ? (
        <div className="mb-5 rounded-xl border border-brand-200 bg-card p-5">
          <p className="mb-4 text-[15px] font-medium">发一张新券</p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="券码"
              placeholder="如 SPRING-50，客户下单时输入"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
            <Input
              label="名称"
              placeholder="如 春季装修节满 500 减 50"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div>
              <p className="mb-1.5 text-[13px] text-muted-foreground">类型</p>
              <div className="flex gap-2">
                {(["fixed", "percent"] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => setForm({ ...form, kind: k, value: "" })}
                    className={cn(
                      "h-11 flex-1 rounded-lg text-[14px] transition-colors",
                      form.kind === k
                        ? "bg-brand-600 text-white"
                        : "bg-secondary text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {k === "fixed" ? "满减" : "打折"}
                  </button>
                ))}
              </div>
            </div>
            <Input
              label={form.kind === "fixed" ? "减多少（元）" : "折扣（填 5 = 95 折）"}
              inputMode="decimal"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
            <Input
              label="门槛（元，不填 = 无门槛）"
              inputMode="decimal"
              value={form.threshold}
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
            />
            <Input
              label="发行量（不填 = 不限量）"
              inputMode="numeric"
              value={form.totalCount}
              onChange={(e) => setForm({ ...form, totalCount: e.target.value })}
            />
          </div>

          {create.error && <ErrorBlock error={create.error} />}
          <div className="mt-5 flex gap-3">
            <Button
              loading={create.isPending}
              disabled={!form.code || !form.name || !form.value}
              onClick={() => create.mutate()}
            >
              发这张券
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              取消
            </Button>
          </div>
        </div>
      ) : (
        <Button className="mb-5" onClick={() => setOpen(true)}>
          <Plus className="size-4" />
          发一张新券
        </Button>
      )}

      {list.isPending ? (
        <Spinner compact />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.length === 0 ? (
        <Empty title="还没发过券" hint="发一张券，客户结算时输券码就能用" compact />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="grid grid-cols-[minmax(0,1fr)_150px_130px_130px_130px_90px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
            <span>券</span>
            <span>券码</span>
            <span>面额 / 门槛</span>
            <span>已用</span>
            <span className="text-right">花了我</span>
            <span className="text-right">状态</span>
          </div>

          {list.data.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-[minmax(0,1fr)_150px_130px_130px_130px_90px] items-center gap-4 border-t border-border px-5 py-3 text-[14px]"
            >
              <span className="min-w-0 truncate">{c.name}</span>
              <span className="font-mono text-[13px] text-brand-600">{c.code}</span>
              <span className="text-[13px] text-muted-foreground">
                {c.kind === "fixed" ? (
                  <>
                    减 <Price cents={c.value} />
                  </>
                ) : (
                  <>打 {(100 - c.value) / 10} 折</>
                )}
                {c.threshold > 0 && (
                  <span className="mt-0.5 block">
                    满 <Price cents={c.threshold} /> 可用
                  </span>
                )}
              </span>
              <span className="text-[13px] text-muted-foreground tabular-nums">
                {c.usedCount}
                {c.totalCount > 0 ? ` / ${c.totalCount}` : " 张（不限量）"}
              </span>
              {/* 🔴 成本单独一列：发券方掏的是自己的差价。 */}
              <span className="text-right">
                <Price cents={c.spent} className="text-red-600" />
              </span>
              <span className="text-right">
                <button
                  className="text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => toggle.mutate(c)}
                  disabled={toggle.isPending}
                >
                  {c.active ? "停用" : "启用"}
                </button>
                {!c.active && (
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">已停用</span>
                )}
              </span>
            </div>
          ))}
          {toggle.error && (
            <div className="px-5 py-3">
              <ErrorBlock error={toggle.error} />
            </div>
          )}
        </div>
      )}
    </AccountShell>
  );
}
